// src/components/claims/claimsModel.js
//
// Pure rules of the «Affermazioni nel testo» / «Fonti consultate» panels and of the blog-seo «Riferimenti»
// (BE ≥ e162398 — contract: <BACKEND_FOLDER>/docs/api-server/endpoints.md § The claims
// list / § The sources list). No React, no i18n.
// ⛔ A row's `source` is shown as the row carries it — never looked up in
//   sources_consulted (a CONTRADICTED row's source may be missing there).
// ⛔ `highlight` is in UTF-16 units = JS string indices: slice, never convert.
// ⛔ Links only for http(s) — a source `url` is null for anything else, and an
//   unchecked href would let a `javascript:` through.

export const STATUS_TONE = Object.freeze({
  FROM_BRIEF: 'neutro',
  FROM_BRAND: 'neutro',
  SUPPORTED: 'verde',
  PARTIAL: 'attenzione',
  CONTRADICTED: 'errore',
  NOT_FOUND: 'attenzione',
  NOT_CHECKED: 'neutro',
});

const ACTION_STATUSES = new Set(['PARTIAL', 'CONTRADICTED', 'NOT_FOUND']);
const UNREVIEWED = new Set(['UNREVIEWED_SKIPPED', 'UNREVIEWED_ERROR']);

const isPlainObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);

export function verificationOf(results) {
  return isPlainObject(results?.verification) ? results.verification : null;
}

export function claimsOf(verification) {
  return Array.isArray(verification?.claims) ? verification.claims.filter(isPlainObject) : [];
}

// Open the panel when the user has something to act on.
export function needsAttention(verification, warnings) {
  const actionRow = claimsOf(verification).some((c) => ACTION_STATUSES.has(c.status));
  const critical = Array.isArray(warnings) && warnings.some((w) => w?.severity === 'critical');
  return actionRow || critical;
}

export function splitHighlight(text, highlight) {
  const s = typeof text === 'string' ? text : '';
  const start = highlight?.start;
  const end = highlight?.end;
  const valid = Number.isInteger(start) && Number.isInteger(end) && start >= 0 && end > start && end <= s.length;
  if (!valid) return { before: s, mark: '', after: '' };
  return { before: s.slice(0, start), mark: s.slice(start, end), after: s.slice(end) };
}

// Rows arrive in catalogue order (part, then ordinal); consecutive rows of the same
// part + ordinal share one group, shown under its heading.
export function groupClaims(claims) {
  return (claims || []).reduce((groups, claim) => {
    const part = claim.location?.part ?? null;
    const ordinal = claim.location?.ordinal ?? null;
    const last = groups[groups.length - 1];
    if (last && last.part === part && last.ordinal === ordinal) {
      return [...groups.slice(0, -1), { ...last, claims: [...last.claims, claim] }];
    }
    return [...groups, {
      key: `${part}-${ordinal}-${groups.length}`,
      part,
      ordinal,
      heading: claim.location?.heading ?? null,
      claims: [claim],
    }];
  }, []);
}

export function safeHttpUrl(url) {
  if (typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!/^https?:\/\//i.test(trimmed)) return null;
  try {
    const parsed = new URL(trimmed);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? trimmed : null;
  } catch (_e) {
    return null;
  }
}

// content.references [{marker, source_id}] → [{marker, title, url}] in the BE's order.
export function resolveReferences(references, sources) {
  if (!Array.isArray(references)) return [];
  const byId = new Map(
    (Array.isArray(sources) ? sources : []).filter((s) => s && s.id).map((s) => [s.id, s]),
  );
  return references
    .filter((r) => r && Number.isInteger(r.marker))
    .map((r) => {
      const source = byId.get(r.source_id) || null;
      return { marker: r.marker, title: source?.title || null, url: safeHttpUrl(source?.url) };
    });
}

// The run card's Fact-Check metric. With a claims list, its outcome decides; without
// one (old runs, pinterest, youtube) the legacy rule: a critic LLM_SERVICE_UNAVAILABLE.
export function factCheckView(results) {
  const verification = verificationOf(results);
  const hasScore = results?.factualConfidence != null;
  if (verification) {
    if (UNREVIEWED.has(verification.outcome)) return { show: true, skipped: true };
    return { show: hasScore, skipped: false };
  }
  const skipped = (results?.warnings ?? []).some(
    (w) => (w?.agent || '').startsWith('critic') && w?.code === 'LLM_SERVICE_UNAVAILABLE',
  );
  return { show: hasScore, skipped };
}

// The BE does not de-duplicate brand-rule items: identical ones are shown once.
export function dedupeItems(items) {
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  return items.filter((item) => {
    const key = JSON.stringify([item?.claim, item?.text, item?.highlight?.start, item?.highlight?.end, item?.correction]);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// «Fonti consultate» blocks: what the user brought first, then what Brian found.
const SOURCE_KIND_ORDER = ['REQUEST_URL', 'UPLOAD', 'COMPANY_DOCS', 'COMPANY_WEBSITE', 'WEB_SEARCH'];

// sources_consulted → [{kind, sources}] in SOURCE_KIND_ORDER, then unknown kinds in
// first-seen order; the backend's order inside a kind. Untitled entries are dropped.
export function groupSources(sources) {
  const list = (Array.isArray(sources) ? sources : []).filter((s) => isPlainObject(s) && s.title);
  const kinds = [...new Set(list.map((s) => s.kind ?? null))];
  const rank = (k) => {
    const i = SOURCE_KIND_ORDER.indexOf(k);
    return i === -1 ? SOURCE_KIND_ORDER.length : i;
  };
  return [...kinds]
    .sort((a, b) => rank(a) - rank(b))
    .map((kind) => ({ kind, sources: list.filter((s) => (s.kind ?? null) === kind) }));
}
